import React, { Component } from 'react';
import { withRouter } from 'react-router-dom';

import Background from '../../containers/Home/Sketch/Sketch';
import ShimmeringBackground from './ShimmeringBackground/ShimmeringBackground';
import SketchContext from '../../context/SketchContext';
import classes from './SiteBackground.module.css';
import { setSiteBackgroundOpacity, getBackgroundOpacity } from '../../lib/siteBackground';
import { DUR, EASE } from '../../lib/motion';
import { isShimmerBackground } from '../../lib/backgroundConfig';

/**
 * SiteBackground
 *
 * Mounts the interactive WebGL background exactly once at app level.
 * Provides the sketch instance to any descendant via SketchContext.
 *
 * Lifecycle decisions:
 *  - contextValue is a plain mutable object so the Provider reference
 *    never changes between renders; consumers (e.g. Home) read
 *    contextValue.sketch imperatively without needing context re-renders.
 *  - The Sketch async init calls onReady(sketch) just before its own
 *    animate() call; supplying onReady lets us set show/animating flags
 *    at the right moment without risk of calling animate() before
 *    darkMatterParticles is initialised.
 *  - withRouter lets us react to route changes to activate/deactivate
 *    the background opacity, keeping the same visual contract as the
 *    original Home-owned version.
 */
class SiteBackground extends Component {
    constructor(props) {
        super(props);
        this.mount = React.createRef();
        // Stable container — mutated by componentDidMount, never replaced.
        this.contextValue = { sketch: null };
    }

    componentDidMount() {
        // Shimmer mode: the new Shimmering Prism background (rendered below) owns the
        // visuals. Skip the legacy Sketch entirely — contextValue.sketch stays null,
        // which every SketchContext consumer already null-guards. Flip SITE_BACKGROUND
        // back to 'legacy' in src/lib/backgroundConfig.js to restore the old background.
        if (isShimmerBackground()) return;

        const onWorkRoute = this.props.location.pathname !== '/';

        this.contextValue.sketch = new Background(
            this.mount.current,
            onWorkRoute
                ? (sketch) => {
                      sketch.show = true;
                      sketch.animating = true;
                      setSiteBackgroundOpacity(0.0, 0.8, 0.3);
                  }
                : null
        );
    }

    componentDidUpdate(prevProps) {
        const { location } = this.props;
        if (location.pathname === prevProps.location.pathname) return;

        const sketch = this.contextValue.sketch;

        if (location.pathname !== '/') {
            // Entering a work-detail page — make background visible.
            // Match WorkDetail's pageRef enter fade (DUR.flow / EASE.out) so the
            // background and route content crossfade in lockstep.
            sketch && sketch.activate();
            setSiteBackgroundOpacity(0.89, DUR.flow, 0, EASE.out);
        } else {
            // Returning to home — fade to section-0 idle opacity so
            // Home section-scroll handlers see the correct baseline.
            // Match WorkDetail's pageRef exit fade (DUR.flow / EASE.in).
            setSiteBackgroundOpacity(getBackgroundOpacity(0, window.innerWidth <= 1024), DUR.flow, 0, EASE.in);
        }
    }

    componentWillUnmount() {
        const sketch = this.contextValue.sketch;
        if (sketch && sketch.destroy) {
            sketch.destroy();
        }
        this.contextValue.sketch = null;
    }

    render() {
        return (
            <SketchContext.Provider value={this.contextValue}>
                {/* id="SiteBackground" is the GSAP handle used by Home
                    scroll handlers to animate canvas opacity. */}
                <div
                    ref={this.mount}
                    id="SiteBackground"
                    className={classes.Background}
                />
                {/* New Shimmering Prism background (Phase 1). Self-contained: owns its
                    own fixed full-screen canvas + lifecycle, sits behind all content.
                    Gated by SITE_BACKGROUND in src/lib/backgroundConfig.js. */}
                {isShimmerBackground() && (
                    <ShimmeringBackground pathname={this.props.location.pathname} />
                )}
                {this.props.children}
            </SketchContext.Provider>
        );
    }
}

export default withRouter(SiteBackground);
